"use client";

import { MessageCircleQuestionMark as MessageCircleQuestion } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How do we begin a partnership?",
  answer: "We start with a discovery session to understand your operational goals. From there, we outline a tailored strategy that aligns with your specific business requirements and long-term vision."
}, {
  question: "What is your pricing structure?",
  answer: "Our engagements are structured based on the scope and complexity of your project. We provide transparent, value-based proposals during our initial consultation to ensure clarity from day one."
}, {
  question: "What technical requirements are needed?",
  answer: "We operate with a lean, cloud-first approach. We integrate seamlessly with your existing infrastructure, requiring minimal setup time and no complex software installations on your end."
}, {
  question: "How do you ensure data security?",
  answer: "Security is embedded in our workflow. We adhere to strict confidentiality protocols and industry-standard data protection practices to safeguard your intellectual property and sensitive information."
}, {
  question: "Can we adjust the scope of work?",
  answer: "Flexibility is a core tenet of our service. We maintain an agile framework that allows us to pivot or scale resources as your business needs evolve throughout the project lifecycle."
}, {
  question: "Do you support specific industry standards?",
  answer: "We bring expertise across various sectors, ensuring compliance with relevant regulatory frameworks and best practices. We align our methodology with your specific industry requirements."
}, {
  question: "Who will be managing our account?",
  answer: "You will be paired with a dedicated lead consultant who serves as your primary point of contact, ensuring consistent communication and a deep understanding of your business objectives."
}, {
  question: "How do we manage project transitions?",
  answer: "We prioritize seamless handovers. Whether you are starting a new initiative or concluding a phase, our documentation and reporting processes ensure total continuity and transparency."
}];
export default function Inquiries() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1315" id="common-inquiries" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12">
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-[120px]">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                  § ITLS_SYS · 02 / COMMON INQUIRIES
                </span>
                <span className="inline-block w-2 h-2 rounded-lg" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>

              {}
              <h2 className="font-default font-medium text-4xl md:text-5xl leading-[1.08] tracking-tight text-foreground mb-6">
                <span>Strategic clarity for </span>
                <span className="relative inline-block italic">
                  <svg className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" preserveAspectRatio="none" fill="none" aria-hidden="true">
                    <path d="M 8 30 Q 4 14, 30 8 Q 60 2, 95 6 Q 116 12, 114 32 Q 110 52, 78 54 Q 40 58, 18 52 Q 2 46, 8 30 Z" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 400,
                    strokeDashoffset: isVisible ? 0 : 400,
                    transition: "stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms",
                    transform: "rotate(-4deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative">discerning</span>
                </span>
                <span> partners.</span>
              </h2>

              <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                Essential insights into our operational philosophy. Transparent, 
                direct, and focused on the results that matter to your organization.
              </p>

              {}
              <Link to="/ConnectPage" className="group inline-flex items-center gap-2 font-default font-medium text-foreground">
                <span className="relative">
                  Still have questions? Connect with us
                  <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 group-hover:w-full transition-all duration-300 ease-out" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1" style={{
                color: "var(--primary)"
              }}>
                  →
                </span>
              </Link>

              {}
              <div className="mt-12 hidden lg:block">
                <svg viewBox="0 0 180 220" className="w-44 h-auto" aria-hidden="true">
                  {}
                  <line x1="90" y1="30" x2="90" y2="55" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="90" cy="22" r="8" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="3">
                    <animate attributeName="opacity" values="1;0.6;1" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  {}
                  <rect x="40" y="55" width="100" height="90" rx="0" fill="var(--chart-2)" stroke="var(--foreground)" strokeWidth="3" />
                  {}
                  <circle cx="68" cy="92" r="5" fill="var(--foreground)" />
                  <circle cx="112" cy="92" r="5" fill="var(--foreground)" />
                  {}
                  <circle cx="58" cy="108" r="6" fill="var(--primary)" opacity="0.7" />
                  <circle cx="122" cy="108" r="6" fill="var(--primary)" opacity="0.7" />
                  {}
                  <path d="M 80 120 Q 90 116, 100 120" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" fill="none" />
                  {}
                  <rect x="55" y="145" width="70" height="55" rx="0" fill="var(--chart-2)" stroke="var(--foreground)" strokeWidth="3" />
                  {}
                  <path d="M 140 130 Q 155 120, 148 100 Q 142 85, 125 95" stroke="var(--foreground)" strokeWidth="3" strokeLinecap="round" fill="var(--chart-2)" />
                  {}
                  <circle cx="155" cy="55" r="14" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="2.5" />
                  <circle cx="140" cy="72" r="5" fill="var(--chart-3)" stroke="var(--foreground)" strokeWidth="2" />
                </svg>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="flex flex-col gap-2">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 rounded-lg border-2 border-border bg-card text-card-foreground shadow-md transition-all duration-300 hover:shadow-lg data-[state=open]:border-primary/40 px-2">
                  <AccordionTrigger className="group hover:no-underline py-6 px-4 md:px-6 [&>svg]:hidden">
                    <div className="flex items-center gap-4 md:gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground shrink-0 w-12">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-lg md:text-xl text-foreground leading-snug flex-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-lg border-2 border-foreground transition-all duration-300 group-data-[state=open]:rotate-45 group-data-[state=open]:bg-primary group-data-[state=open]:border-primary group-data-[state=open]:text-primary-foreground" style={{
                    backgroundColor: "var(--chart-3)",
                    color: "var(--foreground)"
                  }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M8 2 V14 M2 8 H14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 md:px-6 pb-6">
                    <div className="pl-0 md:pl-[4.5rem]">
                      <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                      <Link to="/ConnectPage" className="group/link mt-4 inline-flex items-center gap-2 font-default font-medium text-sm text-foreground">
                        <MessageCircleQuestion className="w-4 h-4" style={{
                      color: "var(--primary)"
                    }} />
                        <span className="relative">
                          Discuss your project
                          <span className="absolute left-0 -bottom-0.5 h-[1.5px] w-0 group-hover/link:w-full transition-all duration-300 ease-out" style={{
                        backgroundColor: "var(--primary)"
                      }} />
                        </span>
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}